import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Search } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { openLineFix } from './TagLineFixDialog';

const when = (s) => (s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const v = (x) => (x === null || x === undefined || x === '' ? '—' : String(x));

const Entry = ({ e, onOpen }) => (
  <div className="rounded-lg border border-border p-3" data-testid="tag-fix-log-entry">
    <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 text-xs">
      <button type="button" onClick={() => onOpen(e.jewellery_id)} className="font-mono text-sm font-bold text-primary hover:underline" data-testid="tag-fix-log-piece">{e.jewellery_no}</button>
      <span className="text-muted-foreground">{when(e.at)}</span>
      <span className="ml-auto font-medium text-foreground" data-testid="tag-fix-log-by">{e.by_name || e.by}{e.by_name && e.by ? <span className="font-normal text-muted-foreground"> · {e.by}</span> : null}</span>
    </div>
    <table className="w-full text-xs">
      <tbody>{e.changes.map((c, i) => (
        <tr key={i} className="border-t border-border/50">
          <td className="w-28 py-1 pr-2 font-semibold text-muted-foreground">{c.code || '—'}</td>
          <td className="w-32 py-1 pr-2">{c.field}</td>
          <td className="py-1 text-red-600 line-through decoration-red-300">{v(c.from)}</td>
          <td className="py-1 font-semibold text-emerald-700">{v(c.to)}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

// Every saved tag-line correction in this company: who changed what and when.
export function TagFixLogDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    notesApi.get('/notes/tag-workspace/admin/line-fix-log').then(({ data }) => setRows(data || [])).catch((e) => { toast.error(extractError(e, 'Could not load the log')); setRows([]); });
  }, []);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((e) => !s || [e.jewellery_no, e.by, e.by_name].some((x) => String(x || '').toLowerCase().includes(s)));
  }, [rows, q]);
  const open = (id) => { onClose(); openLineFix(id); };
  return (
    <div className="fixed inset-0 z-[230] flex items-center justify-center p-4" data-testid="tag-fix-log-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl bg-card p-4 shadow-xl">
        <div className="mb-3 flex items-center gap-3">
          <h3 className="text-base font-bold text-foreground">Corrections log</h3>
          <span className="text-xs text-muted-foreground">{rows ? `${shown.length} correction${shown.length === 1 ? '' : 's'}` : ''}</span>
          <button onClick={onClose} className="ml-auto" aria-label="Close" data-testid="tag-fix-log-close"><X size={18} /></button>
        </div>
        <label className="mb-3 flex items-center gap-2 rounded-lg border border-border px-2.5">
          <Search size={14} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search piece number or person" className="h-9 flex-1 bg-transparent text-sm outline-none" data-testid="tag-fix-log-search" />
        </label>
        <div className="flex-1 space-y-2 overflow-y-auto">
          {!rows ? <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
            : !shown.length ? <p className="py-10 text-center text-sm text-muted-foreground" data-testid="tag-fix-log-empty">No corrections yet.</p>
              : shown.map((e) => <Entry key={e.id} e={e} onOpen={open} />)}
        </div>
      </div>
    </div>
  );
}
